import React, { useEffect, useState } from "react";
import { SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization, useSelfOrganizationUser } from "../../hooks/useOrganization";
import {
  BUSINESS_SURVEY_NAME,
  businessSurvey,
  KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG,
  MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG,
} from "../../surveys/businessSurvey";
import { hasAnswered, questionsWithVisibility, SurveyDefinition } from "../../surveys/survey";
import { SurveyQuestion } from "../../components/get-compliant/survey/SurveyQuestion";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { useDataRequest } from "../../../common/hooks/api";
import { ArrowLongLeftIcon, ArrowLongRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import Confetti from "react-confetti";
import { Api } from "../../../common/service/Api";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { useOrganizationVendors } from "../../hooks";
import { Card, Modal } from "@truevault/aquilo";
import { Shortcuts } from "./DashboardShortcuts";
import { UncategorizedContractorsTable } from "./UncategorizedContractorsTable";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { LEGACY_CONTRACTOR_CATEGORY_SENTINEL } from "../../util/vendors";
import { PageLoading } from "../../components";
import { SetUpTruevaultStepsPanel } from "./SetUpTruevaultStepsPanel";
import {
  isAllOnboardingStepsComplete,
  useOnboardingProgress,
} from "../../../common/hooks/useOnboardingProgress";
import { YOTPO_LEGACY_VENDOR_ID, YotpoVendorMigrationPrompt } from "./YotpoVendorMigrationPrompt";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { sellingSurvey, SHOPIFY_SNI_SLUG } from "../../surveys/sellingSurvey";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { collectedPICsFromFilteredCollection } from "../../../common/service/resources/data-map/dataMapTransformers";
import { getSensitivePICsWithFilters } from "../../../common/service/resources/data-map/picHelpers";
import _ from "lodash";

function useLatestMarketingContent() {
  const api = async () => {
    return Api.marketingWebsiteContent.getLatestContent();
  };

  const { result: latestMarketingContent } = useDataRequest({
    queryKey: ["marketingWebsiteContent"],
    api,
    staleTime: 0,
    keepPreviousData: false,
  });

  return latestMarketingContent;
}

function getGroupsFromSlugs(slugs: string[], groupings: string[][]): string[][] {
  if (!groupings || !Array.isArray(groupings)) {
    return [];
  }
  const groupIndexes = new Set<number>();
  for (const slug of slugs) {
    const groupIndex = groupings.findIndex((group) => group.includes(slug));
    if (groupIndex >= 0) {
      groupIndexes.add(groupIndex);
    }
  }
  return Array.from(groupIndexes).map((index) => groupings[index]);
}

function isQuestionFromSurvey(questionSlug: string, surveyDefinition: SurveyDefinition): boolean {
  return surveyDefinition.questions.some((q) => q.slug === questionSlug);
}

const SHARING_AND_SELLING_SURVEY_SLUGS_TO_SHOW_ON_DASHBOARD = [SHOPIFY_SNI_SLUG];

export default function MainAppDashboard() {
  const [org, ,] = usePrimaryOrganization();
  const currentOrgUser = useSelfOrganizationUser();
  const { data: dataMap } = useDataMap(org?.id);

  const [vendors, , refreshVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ undefined,
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const [onboardingProgress] = useOnboardingProgress(org?.id);

  const hasYotpoVendor = vendors?.some((v) => v.vendorId === YOTPO_LEGACY_VENDOR_ID);
  const [yotpoDoneButtonClicked, setYotpoDoneButtonClicked] = useState(false);

  // State/data fetching for dashboard welcome modal
  const [seenDashboardWelcomeModal, setSeenDashboardWelcomeModal] = useLocalStorage(
    "seen_dashboard_welcome_modal",
    "false",
  );

  // Table for uncategorized contractors

  const [initialDataRecipientIdsToKeepVisible, setInitialDataRecipientIdsToKeepVisible] = useState<
    string[]
  >([]);
  useEffect(() => {
    if (initialDataRecipientIdsToKeepVisible.length > 0 || !vendors || vendors.length === 0) {
      return;
    }

    let uncategorizedContractors = vendors.filter(
      (v) =>
        v.classification?.slug === "contractor" &&
        (!v.category ||
          v.category.length === 0 ||
          v.category === LEGACY_CONTRACTOR_CATEGORY_SENTINEL),
    );
    setInitialDataRecipientIdsToKeepVisible(uncategorizedContractors.map((v) => v.id));
  }, [vendors]);

  const [categorizedDataRecipientIds, setCategorizedDataRecipientIds] = useState<string[]>([]);
  const [categorizeContractorsDoneButtonClicked, setCategorizeContractorsDoneButtonClicked] =
    useState(false);

  let uncategorizedContractors = (vendors || []).filter((v) =>
    initialDataRecipientIdsToKeepVisible.includes(v.id),
  );

  // Show the Done button if categorizedDataRecipientIds is the same as initialDataRecipientIdsToKeepVisible, and initialDataRecipientIdsToKeepVisible is not empty
  let showCategorizeContractorsDoneButton =
    new Set(categorizedDataRecipientIds).size ===
      new Set(initialDataRecipientIdsToKeepVisible).size &&
    initialDataRecipientIdsToKeepVisible.length > 0;

  // Inline survey questions state/hooks

  const { answers: bizAnswers, setAnswer: bizSetAnswer } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);
  const bizSurveyDefinition = businessSurvey(bizAnswers);

  const { answers: sellingAndSharingAnswers, setAnswer: sellingAndSharingSetAnswer } = useSurvey(
    org?.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const sellingSurveyDefinition = sellingSurvey(sellingAndSharingAnswers, vendors || []);

  const [initialQuestionSlugsToKeepVisible, setInitialQuestionSlugsToKeepVisible] = useState<
    string[]
  >([]);
  // Only initialize `initialQuestionSlugsToKeepVisible` once when `bizAnswers` is loaded
  useEffect(() => {
    if (
      initialQuestionSlugsToKeepVisible.length == 0 &&
      vendors &&
      vendors?.length > 0 &&
      bizAnswers &&
      Object.keys(bizAnswers).length > 0 &&
      sellingAndSharingAnswers &&
      Object.keys(sellingAndSharingAnswers).length > 0
    ) {
      const bizQuestionsWithVisibilityState = questionsWithVisibility(
        bizSurveyDefinition,
        bizAnswers,
      );
      const sellingQuestionsWithVisibilityState = questionsWithVisibility(
        sellingSurvey(sellingAndSharingAnswers, vendors),
        sellingAndSharingAnswers,
      );

      const initialBizVisibleAndUnansweredQuestions = bizQuestionsWithVisibilityState
        .filter((q) => {
          // The Maryland sensitive data question is a special-case since: (1) it can only be initially answered from the dashboard (2) only shows up if there are sensitive collected PICs and the data map onboarding step is complete
          if (q.slug === MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG) {
            let marylandSensitivePICNames = marylandCollectedSensitivePICNames(dataMap, bizAnswers);
            return (
              onboardingProgress?.dataMapOnboardingProgress?.allVendorsComplete &&
              marylandSensitivePICNames?.length > 0 &&
              !hasAnswered(bizAnswers, q, false)
            );
          }

          // Hide all business survey questions other than the Maryland sensitive data question (POL-6630)
          return false;
        })
        .map((q) => q.slug);
      const initialSellingAndSharingVisibleAndUnansweredQuestions = [
        ...sellingQuestionsWithVisibilityState,
      ]
        .filter((q) => SHARING_AND_SELLING_SURVEY_SLUGS_TO_SHOW_ON_DASHBOARD.includes(q.slug))
        .filter((q) => q.visible && !hasAnswered(sellingAndSharingAnswers, q, false))
        .map((q) => q.slug);

      const allQuestions = [
        ...initialBizVisibleAndUnansweredQuestions,
        ...initialSellingAndSharingVisibleAndUnansweredQuestions,
      ];

      setInitialQuestionSlugsToKeepVisible(allQuestions);
    }
  }, [bizAnswers, sellingAndSharingAnswers, vendors, org]);

  const bizQuestionsWithVisibilityState = questionsWithVisibility(bizSurveyDefinition, bizAnswers);
  const sellingQuestionsWithVisibilityState = questionsWithVisibility(
    sellingSurveyDefinition,
    sellingAndSharingAnswers,
  );
  const bizSlugGroupsToShow = getGroupsFromSlugs(
    initialQuestionSlugsToKeepVisible,
    bizSurveyDefinition.relatedQuestionGroups || [],
  );
  const sellingSlugGroupsToShow = getGroupsFromSlugs(
    initialQuestionSlugsToKeepVisible,
    sellingSurveyDefinition.relatedQuestionGroups || [],
  );

  const slugGroupsToShow = [...bizSlugGroupsToShow, ...sellingSlugGroupsToShow];
  const [currentQuestionSlugGroupIndex, setCurrentQuestionSlugGroupIndex] = useState(0);

  const latestMarketingContent = useLatestMarketingContent();
  const latestWebsiteArticle = latestMarketingContent?.items?.find(
    (c) => c.contentType === "LATEST_WEBSITE_ARTICLE",
  );
  const latestProductUpdate = latestMarketingContent?.items?.find(
    (c) => c.contentType === "LATEST_PRODUCT_UPDATE_EMAIL",
  );

  // Computed UI values

  const fullName = currentOrgUser?.firstName + " " + currentOrgUser?.lastName;
  const welcomeMessage = currentOrgUser ? `Welcome, ${fullName}` : `Welcome`;

  // State for animation and visual decorations

  const [animationDirection, setAnimationDirection] = useState<"previous" | "next">("next");
  const [isConfettiVisible, setIsConfettiVisible] = useState(false);

  if (!vendors) {
    return <PageLoading />;
  }

  const isAllOnboardingComplete = isAllOnboardingStepsComplete(org, onboardingProgress);

  // Order of priority to show:
  // 1. Yotpo vendor migration prompt
  // 2. Uncategorized contractors table
  // 3. Unanswered survey questions
  const showYotpoMigrationPrompt = hasYotpoVendor && !yotpoDoneButtonClicked;
  const showUncategorizedContractorsTable =
    !showYotpoMigrationPrompt &&
    uncategorizedContractors?.length > 0 &&
    !categorizeContractorsDoneButtonClicked;
  const showUnansweredSurveyQuestions =
    !showYotpoMigrationPrompt && !showUncategorizedContractorsTable && slugGroupsToShow.length > 0;

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent={`${welcomeMessage} 👋`} />

      {!isAllOnboardingComplete && seenDashboardWelcomeModal !== "true" && (
        <DashboardWelcomeModal
          setSeenDashboardWelcomeModal={() => setSeenDashboardWelcomeModal("true")}
        />
      )}

      {!isAllOnboardingComplete && (
        <div style={{ marginBottom: "32px" }}>
          <SetUpTruevaultStepsPanel />
        </div>
      )}

      {slugGroupsToShow.length > 0 && currentQuestionSlugGroupIndex === slugGroupsToShow.length && (
        <div className="dashboard--survey-finished-wrapper">
          {isConfettiVisible && (
            <Confetti width={630} height={380} confettiSource={{ w: 10, h: 10, x: 503, y: 280 }} />
          )}
          <img
            src={`/assets/images/stay_compliant/dashboard/dashboard-finish-survey-illustration.svg`}
            alt="Survey finished"
          />
          <div className="dashboard--survey-finished-text">Hooray! 🎉 You're all caught up!</div>
        </div>
      )}

      {showYotpoMigrationPrompt && isAllOnboardingComplete && (
        <>
          <div className="tw-boundary">
            <UrgentItemsHeading />
          </div>
          <div className="dashboard--survey-question-pane-without-animation">
            <div className="tw-boundary">
              <AnswerQuestionsImmediatelyWarning />
            </div>

            <YotpoVendorMigrationPrompt onComplete={() => setYotpoDoneButtonClicked(true)} />
          </div>
        </>
      )}

      {showUncategorizedContractorsTable && isAllOnboardingComplete && (
        <div className="tw-boundary">
          <UrgentItemsHeading />
          <div className="bg-yellow-100 border-[1.5px] border-solid border-yellow-400 mt-3 p-6 max-w-[912px] rounded-lg">
            <AnswerQuestionsImmediatelyWarning />
            <div className="text-neutral-900 font-bold text-lg mb-3">
              Review and add missing information to your list of contractors below
            </div>
            <div className="p-3">
              <div className="text-gray-900 mb-3 font-medium">Your Contractors</div>
              <UncategorizedContractorsTable
                orgId={org?.id}
                contractors={uncategorizedContractors}
                refreshVendors={refreshVendors}
                removeVendor={async (dataRecipient: OrganizationDataRecipientDto) => {
                  await Api.organizationVendor.removeVendor(org?.id, dataRecipient.vendorId);
                  refreshVendors();
                  setInitialDataRecipientIdsToKeepVisible((prev) =>
                    prev.filter((id) => id !== dataRecipient.id),
                  );
                }}
                markAsCategorized={(dataRecipientId: string) => {
                  setCategorizedDataRecipientIds((prev) => {
                    return prev.includes(dataRecipientId) ? prev : [...prev, dataRecipientId];
                  });
                }}
              />
            </div>
            {showCategorizeContractorsDoneButton && (
              <div className="mt-4">
                <ContractorDoneButton
                  onClick={() => setCategorizeContractorsDoneButtonClicked(true)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {showUnansweredSurveyQuestions &&
        currentQuestionSlugGroupIndex < slugGroupsToShow.length &&
        isAllOnboardingComplete && (
          <>
            <div className="tw-boundary">
              <UrgentItemsHeading />
            </div>
            <div className="dashboard--survey-question-wrapper">
              {slugGroupsToShow.map((questionSlugGroup, idx) => {
                return (
                  <div
                    key={questionSlugGroup[0]}
                    className={clsx("dashboard--survey-question-pane", animationDirection, {
                      "active-survey-question": idx == currentQuestionSlugGroupIndex,
                    })}
                  >
                    <div className="tw-boundary">
                      <AnswerQuestionsImmediatelyWarning />
                    </div>

                    <div className="dashboard--survey-question-progress-number">
                      Question {currentQuestionSlugGroupIndex + 1} out of {slugGroupsToShow.length}
                    </div>

                    {questionSlugGroup.map((currentSlug, idx) => {
                      const currentQuestion =
                        bizQuestionsWithVisibilityState.find((q) => q.slug == currentSlug) ||
                        sellingQuestionsWithVisibilityState.find((q) => q.slug == currentSlug);
                      return (
                        <>
                          {currentQuestion.slug ===
                            MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG && (
                            <div style={{ fontWeight: 500 }}>
                              According to your Data Map, you collect the following sensitive data
                              categories:
                              <ul>
                                {marylandCollectedSensitivePICNames(dataMap, bizAnswers).map(
                                  (name, i) => (
                                    <li key={i}>{name}</li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                          <SurveyQuestion
                            key={currentQuestion.slug}
                            question={currentQuestion}
                            answer={
                              bizAnswers[currentQuestion.slug] ||
                              sellingAndSharingAnswers[currentQuestion.slug]
                            }
                            onAnswer={async (answer) => {
                              let allVisibleInGroupAnswered = false;

                              if (isQuestionFromSurvey(currentQuestion.slug, bizSurveyDefinition)) {
                                await bizSetAnswer(
                                  currentQuestion.slug,
                                  answer,
                                  currentQuestion.plaintextQuestion,
                                );

                                // Instead of waiting for a server response with latest answer data, apply the answer locally to immediately compute the next survey state
                                const newAnswers = {
                                  ...bizAnswers,
                                  [currentQuestion.slug]: answer,
                                };
                                const newBizSurveyDefinition = businessSurvey(newAnswers);
                                const newQuestionsWithVisibility = questionsWithVisibility(
                                  newBizSurveyDefinition,
                                  newAnswers,
                                );
                                // Find the grouping of the current question
                                const allSlugsInGroup =
                                  newBizSurveyDefinition.relatedQuestionGroups.find((group) =>
                                    group.includes(currentQuestion.slug),
                                  );
                                allVisibleInGroupAnswered = allSlugsInGroup.every((slug) => {
                                  const q = newQuestionsWithVisibility.find((q) => q.slug == slug);
                                  return !q.visible || hasAnswered(newAnswers, q, false);
                                });
                              } else if (
                                isQuestionFromSurvey(currentQuestion.slug, sellingSurveyDefinition)
                              ) {
                                await sellingAndSharingSetAnswer(
                                  currentQuestion.slug,
                                  answer,
                                  currentQuestion.plaintextQuestion,
                                );

                                if (currentQuestion.slug === SHOPIFY_SNI_SLUG) {
                                  allVisibleInGroupAnswered = true;
                                }
                              }

                              if (!allVisibleInGroupAnswered) {
                                return;
                              }

                              if (currentQuestionSlugGroupIndex === slugGroupsToShow.length - 1) {
                                setIsConfettiVisible(true);

                                setTimeout(() => {
                                  setIsConfettiVisible(false);
                                }, 2500);
                                setCurrentQuestionSlugGroupIndex((idx) => idx + 1);
                              } else {
                                setAnimationDirection("next");
                                setTimeout(() => {
                                  setCurrentQuestionSlugGroupIndex((idx) => idx + 1);
                                }, 250);
                              }
                            }}
                            collapseTopSpacing={idx == 0}
                          />
                        </>
                      );
                    })}

                    {slugGroupsToShow.length > 1 && (
                      <div className="dashboard--survey-question-navigation-buttons">
                        {currentQuestionSlugGroupIndex > 0 ? (
                          <div
                            role="button"
                            onClick={() => {
                              setAnimationDirection("previous");
                              setTimeout(() => {
                                setCurrentQuestionSlugGroupIndex((idx) => idx - 1);
                              }, 250);
                            }}
                          >
                            <ArrowLongLeftIcon /> Previous Question
                          </div>
                        ) : (
                          <div></div>
                        )}

                        {currentQuestionSlugGroupIndex < slugGroupsToShow.length - 1 ? (
                          <div
                            role="button"
                            onClick={() => {
                              setAnimationDirection("next");
                              setTimeout(() => {
                                setCurrentQuestionSlugGroupIndex((idx) => idx + 1);
                              }, 250);
                            }}
                          >
                            {currentQuestionSlugGroupIndex === slugGroupsToShow.length - 1 &&
                            questionSlugGroup[0] &&
                            (() => {
                              const question =
                                bizQuestionsWithVisibilityState.find(
                                  (q) => q.slug === questionSlugGroup[0],
                                ) ||
                                sellingQuestionsWithVisibilityState.find(
                                  (q) => q.slug === questionSlugGroup[0],
                                );
                              return question?.type === "checkboxes";
                            })() ? (
                              <div className="dashboard--survey-question-navigation-button-done">
                                Done
                              </div>
                            ) : (
                              <>
                                Next Question <ArrowLongRightIcon />
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="dashboard--empty-space"></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

      <Shortcuts org={org} onboardingProgress={onboardingProgress} vendorCount={vendors?.length} />

      {(latestWebsiteArticle || latestProductUpdate) && (
        <>
          <div className="dashboard--latest-website-article-header">Updates for you</div>
          <div className="d-flex tw-boundary" style={{ gap: "16px" }}>
            {latestWebsiteArticle && (
              <Card
                dropShadow={true}
                title={latestWebsiteArticle.title}
                description={latestWebsiteArticle.description}
                buttonText="Read Article"
                onButtonClick={() => window.open(latestWebsiteArticle.url)}
                icon={EXTERNAL_ARTICLE_ICON}
              />
            )}
            {latestProductUpdate && (
              <Card
                dropShadow={true}
                title={latestProductUpdate.title}
                description={latestProductUpdate.description}
                buttonText="See What's New"
                onButtonClick={() => window.open(latestProductUpdate.url)}
                icon={LATEST_PRODUCT_UPDATE_ICON}
              />
            )}
          </div>
        </>
      )}
    </StandardPageWrapper>
  );
}

function DashboardWelcomeModal({
  setSeenDashboardWelcomeModal,
}: {
  setSeenDashboardWelcomeModal: (value: string) => void;
}) {
  return (
    <div className="tw-boundary">
      <Modal
        title="Welcome to your TrueVault Dashboard!"
        alignText="center"
        firstButton="Let's Get Started"
        firstButtonColor="primary"
        onFirstButtonClick={() => {
          setSeenDashboardWelcomeModal("true");
        }}
        widthButton="full"
        showOverlay
        closeButton
        onClose={() => {
          setSeenDashboardWelcomeModal("true");
        }}
      >
        <p className="text-gray-500 text-[14px] text-center">
          Follow the steps to complete your compliance setup.
        </p>
        <img
          className="w-[278px] h-[280px] mt-[40px] mx-auto"
          src="/assets/images/stay_compliant/dashboard/welcome-dashboard-modal-illustration.svg"
          alt="Dashboard"
        />
      </Modal>
    </div>
  );
}

const EXTERNAL_ARTICLE_ICON = (
  <img
    src="/assets/images/globe-icon-dashboard.png"
    alt="External Article"
    style={{ width: "52px", height: "52px" }}
  />
);

const LATEST_PRODUCT_UPDATE_ICON = (
  <img
    src="/assets/images/nav-icon-truevault.png"
    alt="Latest Product Update"
    style={{ width: "52px", height: "52px" }}
  />
);

const UrgentItemsHeading = () => (
  <div className="text-[#4D260E] text-xl font-medium">Urgent items requiring your attention!</div>
);

const AnswerQuestionsImmediatelyWarning = () => (
  <div className="flex items-center gap-3 mb-3">
    <ExclamationTriangleIcon className="text-[#FBBF24] w-[16px] h-[14px]" />
    <span className="text-yellow-800 text-base font-medium">
      Answer these questions immediately so you don't fall out of compliance
    </span>
  </div>
);

const ContractorDoneButton = ({ onClick }: { onClick?: () => void }) => (
  <div
    role="button"
    className="w-[190px] text-center rounded-md py-2 cursor-pointer border border-solid border-yellow-700 text-yellow-800 text-base font-medium"
    onClick={onClick}
  >
    Done
  </div>
);

// Returns list of PIC names that are (1) collected from a consumer collection group and (2) are considered sensitive in Maryland
function marylandCollectedSensitivePICNames(
  dataMap: DataMapDto,
  bizSurveyAnswers: SurveyAnswers,
): string[] {
  if (!dataMap) return [];

  let marylandSensitivePICIds = getSensitivePICsWithFilters(dataMap)
    .filter((pic) => pic.sensitiveRegions?.includes("md") || pic.sensitiveRegions?.includes("MD"))
    .map((pic) => pic.id);
  let consumerCollectedPICs = collectedPICsFromFilteredCollection(dataMap, {
    collectionGroupType: ["BUSINESS_TO_BUSINESS", "BUSINESS_TO_CONSUMER"],
  });

  let result = _.uniq(
    consumerCollectedPICs.filter((pic) => marylandSensitivePICIds.includes(pic.id)),
  ).map((pic) => pic.name);

  if (bizSurveyAnswers && bizSurveyAnswers[KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG] == "true") {
    result.push("Children's data (under 13)");
  }

  return result;
}
